import { useState } from "react";
import type { PlaceType, PricePoint, PricedPricing, Pricing, UpdatePlaceData } from "~/lib/api";
import { PanelSection } from "~/components/ui/panel-section";
import { Switch } from "~/components/ui/switch";
import { DEFAULT_CURRENCY, addPrice, removePrice, updatePrice } from "./currencies";
import { CurrencySelect } from "./CurrencySelect";
import { PricePointList } from "./PricePointList";
import { AddPricePoint } from "./AddPricePoint";
import { getPricingTypes, supportsFreePricing, type PricingTypeDefinition } from "./pricing-types";

export interface PricingSectionProps {
  pricing: Pricing | null | undefined;
  placeType: PlaceType | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
}

export function PricingSection({ pricing, placeType, canEdit, onUpdate }: PricingSectionProps) {
  const [pendingCurrency, setPendingCurrency] = useState(DEFAULT_CURRENCY);

  const view = derivePricingView(pricing, placeType, !!canEdit);
  if (!view) return null;

  const { priced, pricingTypes, isFree, canBeFree } = view;
  const currency = priced?.currency ?? pendingCurrency;
  const save = (updated: Pricing | null) => onUpdate?.({ pricing: updated });
  const handleCurrencyChange = (v: string) => (priced ? save({ ...priced, currency: v }) : setPendingCurrency(v));
  const handleAdd = (point: PricePoint) => save(addPrice(priced, currency, point));
  const handleFreeToggle = (checked: boolean) => save(checked ? { free: true } : null);

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Header>
          <PanelSection.Title className="px-0">Pricing</PanelSection.Title>
          {canEdit && !isFree && <CurrencySelect value={currency} onChange={handleCurrencyChange} />}
        </PanelSection.Header>
        <PanelSection.Content>
          {canEdit && canBeFree && (
            <label className="flex items-center justify-between gap-3 text-sm">
              Free access
              <Switch checked={isFree} onCheckedChange={handleFreeToggle} aria-label="Free access" />
            </label>
          )}
          {!isFree && priced && priced.prices.length > 0 && (
            <PricePointList
              pricing={priced}
              canEdit={canEdit}
              onChange={(i, p) => save(updatePrice(priced, i, p))}
              onRemove={(i) => save(removePrice(priced, i))}
            />
          )}
          {!isFree && canEdit && pricingTypes && (
            <AddPricePoint
              pricingTypes={pricingTypes}
              existingPrices={priced?.prices ?? []}
              currency={currency}
              onAdd={handleAdd}
            />
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}

interface PricingView {
  priced: PricedPricing | null;
  pricingTypes: PricingTypeDefinition[] | null;
  isFree: boolean;
  canBeFree: boolean;
}

function derivePricingView(
  pricing: Pricing | null | undefined,
  placeType: PlaceType | null,
  canEdit: boolean,
): PricingView | null {
  const isFree = pricing?.free === true;
  const priced = pricing && !pricing.free ? pricing : null;
  const pricingTypes = placeType ? getPricingTypes(placeType) : null;
  const canBeFree = placeType ? supportsFreePricing(placeType) : false;
  const hasPricing = !!priced?.prices.length;
  if (!canEdit && (isFree || !hasPricing)) return null;
  if (canEdit && !canBeFree && !pricingTypes) return null;
  return { priced, pricingTypes, isFree, canBeFree };
}
